import React, { useState, useCallback } from "react";
import { useAuditLogs } from "../../../hooks/useAuditLogs";
import Pagination from "../../../components/admin/Pagination";
import SearchFilter from "../../../components/admin/SearchFilter";

const ACTION_OPTIONS = [
  { value: "update", label: "Update" },
  { value: "status_change", label: "Status Change" },
  { value: "delete", label: "Delete" },
];

const TARGET_OPTIONS = [
  { value: "public_donor", label: "Public Donor" },
  { value: "donor", label: "Registered Donor" },
  { value: "blood_request", label: "Blood Request" },
];

const ACTION_BADGES = {
  update: "badge-info",
  status_change: "badge-warning",
  delete: "badge-error",
};

const TARGET_LABELS = {
  public_donor: "Public Donor",
  donor: "Registered Donor",
  blood_request: "Blood Request",
};

const formatChanges = (changes) => {
  if (!changes || Object.keys(changes).length === 0) return "—";
  return Object.entries(changes).map(([field, { from, to }]) => (
    <span key={field} className="block text-xs">
      <span className="font-medium text-gray-700">{field}:</span>{" "}
      <span className="text-red-500 line-through">{String(from)}</span>
      {" → "}
      <span className="text-green-600">{String(to)}</span>
    </span>
  ));
};

const AdminAuditLogs = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ targetType: "", action: "" });

  const queryParams = {
    page,
    limit: 20,
    ...(search && { search }),
    ...(filters.targetType && { targetType: filters.targetType }),
    ...(filters.action && { action: filters.action }),
  };

  const { data, isLoading, error } = useAuditLogs(queryParams);

  const handleSearchChange = useCallback((val) => {
    setSearch(val);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((name, val) => {
    setFilters((prev) => ({ ...prev, [name]: val }));
    setPage(1);
  }, []);

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Failed to load audit logs: {error.message}</span>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Audit Logs</h1>

      <SearchFilter
        searchValue={search}
        onSearchChange={handleSearchChange}
        filters={[
          {
            name: "targetType",
            label: "All Types",
            value: filters.targetType,
            options: TARGET_OPTIONS,
          },
          {
            name: "action",
            label: "All Actions",
            value: filters.action,
            options: ACTION_OPTIONS,
          },
        ]}
        onFilterChange={handleFilterChange}
      />

      {isLoading ? (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Date</th>
                <th>Admin</th>
                <th>Action</th>
                <th>Target</th>
                <th>Name</th>
                <th>Changes</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, i) => (
                <tr key={i}>
                  {[...Array(6)].map((_, j) => (
                    <td key={j}>
                      <div className="skeleton h-4 w-full"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !data?.data || data.data.length === 0 ? (
        <div className="text-center py-12 text-gray-600">No audit logs found</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="overflow-x-auto hidden md:block">
            <table className="table table-zebra w-full">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Admin</th>
                  <th>Role</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Name</th>
                  <th>Changes</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((log) => (
                  <tr key={log._id}>
                    <td className="text-xs whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="text-xs">{log.adminEmail}</td>
                    <td>
                      <span className={`badge badge-sm ${log.adminRole === "super_admin" ? "badge-error" : "badge-info"}`}>
                        {log.adminRole === "super_admin" ? "Super Admin" : "Admin"}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-sm ${ACTION_BADGES[log.action] || "badge-ghost"}`}>
                        {log.action.replace("_", " ")}
                      </span>
                    </td>
                    <td className="text-xs">{TARGET_LABELS[log.targetType] || log.targetType}</td>
                    <td className="text-xs font-medium">{log.targetName || "—"}</td>
                    <td>{formatChanges(log.changes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {data.data.map((log) => (
              <div key={log._id} className="card bg-base-100 shadow-sm">
                <div className="card-body p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className={`badge badge-sm ${ACTION_BADGES[log.action] || "badge-ghost"}`}>
                      {log.action.replace("_", " ")}
                    </span>
                    <span className={`badge badge-sm ${log.adminRole === "super_admin" ? "badge-error" : "badge-info"}`}>
                      {log.adminRole === "super_admin" ? "Super Admin" : "Admin"}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(log.createdAt).toLocaleString()}
                  </div>
                  <div className="text-xs">
                    <span className="font-medium">{log.adminEmail}</span>
                  </div>
                  <div className="text-xs">
                    <span className="font-medium">{TARGET_LABELS[log.targetType]}</span>
                    {log.targetName && <span> — {log.targetName}</span>}
                  </div>
                  {log.changes && Object.keys(log.changes).length > 0 && (
                    <div className="border-t pt-2">
                      {formatChanges(log.changes)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {data.pagination && (
            <div className="flex justify-between items-center mt-4">
              <span className="text-sm text-gray-700">
                Showing {((data.pagination.page - 1) * data.pagination.limit) + 1}–
                {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} of{" "}
                {data.pagination.total}
              </span>
              <Pagination
                currentPage={data.pagination.page}
                totalPages={data.pagination.totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminAuditLogs;
